import { useCallback, useEffect, useState } from 'react'

import { ApiError, api, can, type ConnectorInstance, type Probe } from '../../lib/api'
import { entraProofApi, PROOF_CHECKS, type EntraProof } from '../../lib/entraProof'
import { Dialog, Status, Toggletip, type Tone } from '../../components/kit'
import '../../styles/identity.css'

// The proof that a leaving vendor's accounts are closed, switched on beside
// the sign-in it shares a card with.
//
// # WHY IT SAVES THROUGH ITS OWN ROUTE
//
// The connector save puts the card back to draft, and a card in draft is not
// used for sign-in. Switching the proof on that way would sign everybody out,
// so this section saves through a route that changes the proof's four settings
// and nothing else. Test runs the card's usual test and shows only the proof's
// checks: sign-in's own are shown by the section above.
//
// # WHEN THE SERVER DOES NOT OFFER THE ROUTE
//
// An older server answers 404. The section then reads what it can from the
// card the page already loaded, says it cannot be saved here yet, and shows
// the refusal in plain words if Save is pressed anyway.

const MASK = '········'

const PERMISSIONS =
  'Application permissions with admin consent. User.Read.All is required. ' +
  'AuditLog.Read.All shows who disabled each account. ' +
  'GroupMember.Read.All is for vendors found by group.'

type Form = { enabled: boolean; tenantId: string; clientId: string; secret: string }

function formOf(p: EntraProof): Form {
  return { enabled: p.enabled, tenantId: p.graphTenantId, clientId: p.graphClientId, secret: '' }
}

// What the page already holds, for a server without the route.
function fromCard(card: ConnectorInstance | null): EntraProof {
  if (!card || card.templateKey !== 'entra_id') {
    return {
      connected: false, enabled: false, graphTenantId: '', graphClientId: '',
      graphClientSecretSet: false, collecting: false, probe: null, note: '',
    }
  }
  const str = (k: string) => (typeof card.config[k] === 'string' ? String(card.config[k]).trim() : '')
  const enabled = ['yes', 'true'].includes(str('offboardingProof').toLowerCase())
  return {
    connected: true,
    connectorId: card.id,
    enabled,
    graphTenantId: str('graphTenantId'),
    graphClientId: str('graphClientId'),
    graphClientSecretSet: str('graphClientSecret') !== '',
    signIn: card.status,
    collecting: enabled && (card.status === 'ACTIVE' || card.status === 'FAILING'),
    lastError: card.lastError,
    probe: null,
    note: '',
  }
}

type Verdict = { tone: Tone; label: string; reason: string }

function verdictOf(p: EntraProof): Verdict {
  if (!p.connected) return { tone: 'neutral', label: 'Off', reason: 'Connect Microsoft Entra ID for sign-in first' }
  if (!p.enabled) return { tone: 'neutral', label: 'Off', reason: '' }
  if (!p.graphClientId || !p.graphClientSecretSet) {
    return { tone: 'warn', label: 'Needs attention', reason: 'Add the application id and its secret' }
  }
  if (!p.collecting) {
    return { tone: 'warn', label: 'Needs attention', reason: 'Sign-in is not proved yet, so nothing is read' }
  }
  if (p.lastError) return { tone: 'warn', label: 'Needs attention', reason: 'The last read was refused' }
  return { tone: 'ok', label: 'On', reason: '' }
}

function checkState(c: Probe): { tone: Tone; label: string } {
  if (c.unproven) return { tone: 'warn', label: 'Unproven' }
  if (c.ok) return { tone: 'ok', label: 'Proved' }
  return { tone: 'crit', label: 'Refused' }
}

function when(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
}

export function OffboardingProof({
  card,
  onCardChanged,
}: {
  // The identity card the page loaded. A new one means the card was read
  // again, so this section reads again too; it is also the fallback for a
  // server without the route.
  card: ConnectorInstance | null
  // Asks the page to read the card again without resetting its own form.
  onCardChanged: () => void
}) {
  const [proof, setProof] = useState<EntraProof | null>(null)
  const [old, setOld] = useState(false)
  const [form, setForm] = useState<Form>({ enabled: false, tenantId: '', clientId: '', secret: '' })
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState('')
  const [issues, setIssues] = useState<string[]>([])
  const [checks, setChecks] = useState<Probe[] | null>(null)
  const [signInFailed, setSignInFailed] = useState(false)
  const [details, setDetails] = useState(false)
  const [error, setError] = useState('')

  const mayWrite = can('user.administer')
  const mayTest = can('config.write')

  const load = useCallback(() => {
    entraProofApi
      .get()
      .then((p) => {
        setOld(false)
        setProof(p)
        setForm(formOf(p))
        setDirty(false)
        setError('')
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          const p = fromCard(card)
          setOld(true)
          setProof(p)
          setForm(formOf(p))
          setDirty(false)
          setError('')
          return
        }
        setError(err instanceof Error ? err.message : 'The offboarding proof could not be read')
      })
  }, [card])
  useEffect(load, [load])

  if (error) {
    return (
      <section className="panel proof-panel" aria-labelledby="proof-h">
        <h2 id="proof-h">Offboarding proof</h2>
        <p className="note warn">{error}</p>
      </section>
    )
  }
  if (!proof) return null

  const verdict = verdictOf(proof)
  const lockedInputs = !mayWrite || busy !== ''

  function set<K extends keyof Form>(k: K, v: Form[K]) {
    setForm((f) => ({ ...f, [k]: v }))
    setDirty(true)
    setChecks(null)
  }

  async function save() {
    setBusy('save')
    setIssues([])
    try {
      const secret = form.secret.trim()
      const next = await entraProofApi.save({
        enabled: form.enabled,
        graphTenantId: form.tenantId.trim(),
        graphClientId: form.clientId.trim(),
        ...(secret !== '' && secret !== MASK ? { graphClientSecret: secret } : {}),
      })
      setProof(next)
      setForm(formOf(next))
      setDirty(false)
      onCardChanged()
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setIssues(['This server cannot save the proof yet. It needs the newer API'])
      } else if (err instanceof ApiError && err.issues.length > 0) {
        setIssues(err.issues.map((i) => i.detail))
      } else {
        setIssues([err instanceof Error ? err.message : 'That was not saved'])
      }
    } finally {
      setBusy('')
    }
  }

  async function test() {
    if (!proof?.connectorId) return
    setBusy('test')
    setIssues([])
    try {
      const res = await api.testConnector(proof.connectorId)
      const mine = res.checks.filter((c) => PROOF_CHECKS.includes(c.check))
      setChecks(mine)
      setSignInFailed(mine.length === 0 && !res.allOk)
      // The test also proves sign-in again, which can change the card's
      // status; reading the card again reads this section again.
      onCardChanged()
    } catch (err) {
      setIssues([err instanceof Error ? err.message : 'The test did not run'])
    } finally {
      setBusy('')
    }
  }

  const secretHint = proof.graphClientSecretSet ? 'Stored. Type to replace it' : 'Paste it here'
  const open = checks?.filter((c) => !c.ok || c.unproven) ?? []

  return (
    <section className="panel proof-panel" aria-labelledby="proof-h">
      <div className="p1-head">
        <div>
          <h2 id="proof-h">Offboarding proof</h2>
          <p className="obs-what proof-sub">
            Checks your directory for a leaving vendor's accounts
            <Toggletip label="Microsoft Graph permissions">{PERMISSIONS}</Toggletip>
          </p>
        </div>
        <div className="std-meta">
          <Status tone={verdict.tone}>{verdict.label}</Status>
        </div>
      </div>

      {verdict.reason && <p className="proof-line">{verdict.reason}</p>}
      {old && <p className="proof-line warn">This server cannot switch the proof on yet</p>}
      {proof.collecting && proof.lastObservedAt && (
        <p className="proof-line">Last read {when(proof.lastObservedAt)}</p>
      )}

      {proof.connected && (
        <>
          <label className="proof-switch">
            <input
              type="checkbox"
              role="switch"
              checked={form.enabled}
              disabled={lockedInputs}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            <span>Read a leaving vendor's accounts every 15 minutes</span>
          </label>

          <div className="field-row three">
            <label>
              Directory id
              <input
                value={form.tenantId}
                onChange={(e) => set('tenantId', e.target.value)}
                placeholder="Same as sign-in"
                disabled={lockedInputs}
                spellCheck={false}
              />
            </label>
            <label>
              Application id
              <input
                value={form.clientId}
                onChange={(e) => set('clientId', e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
                disabled={lockedInputs}
                spellCheck={false}
              />
            </label>
            <label>
              Client secret
              <input
                type="password"
                value={form.secret}
                onChange={(e) => set('secret', e.target.value)}
                placeholder={secretHint}
                autoComplete="new-password"
                disabled={lockedInputs}
              />
            </label>
          </div>
        </>
      )}

      {issues.map((i) => (
        <p key={i} className="note warn">
          {i}
        </p>
      ))}

      {proof.connected && mayWrite && (
        <div className="p1-actions save-row">
          <button
            type="button"
            className="button"
            disabled={busy !== '' || !dirty}
            onClick={() => void save()}
            data-action="tprm.settings-sso.a06"
          >
            {busy === 'save' ? 'Saving' : 'Save'}
          </button>
          {mayTest && proof.connectorId && (
            <button
              type="button"
              className="button"
              disabled={busy !== '' || dirty || !proof.enabled}
              onClick={() => void test()}
              data-action="tprm.settings-sso.a07"
            >
              {busy === 'test' ? 'Testing' : 'Test'}
            </button>
          )}
          {dirty && <span className="save-hint">Unsaved. Save before testing</span>}
        </div>
      )}

      {signInFailed && <p className="proof-line warn">Sign-in did not pass its test, so the proof was not tried</p>}
      {checks && checks.length > 0 && (
        <div className="proof-checks" role="status">
          <ul>
            {checks.map((c) => {
              const s = checkState(c)
              return (
                <li key={c.check}>
                  <Status tone={s.tone}>{s.label}</Status>
                  <span>{c.check}</span>
                </li>
              )
            })}
          </ul>
          <button type="button" className="linkish" onClick={() => setDetails(true)}>
            {open.length > 0 ? 'What to do' : 'Details'}
          </button>
        </div>
      )}
      {!checks && verdict.reason === 'The last read was refused' && (
        <button type="button" className="linkish" onClick={() => setDetails(true)}>
          Why
        </button>
      )}

      <Dialog shape="drawer" kind="record" open={details} onClose={() => setDetails(false)} title="Offboarding proof">
        <div className="proof-details">
          {(checks ?? []).map((c) => {
            const s = checkState(c)
            return (
              <div key={c.check} className="proof-detail">
                <div>
                  <Status tone={s.tone}>{s.label}</Status> <strong>{c.check}</strong>
                </div>
                <p>{c.detail}</p>
              </div>
            )
          })}
          {proof.lastError && (
            <div className="proof-detail">
              <strong>The last read</strong>
              <p>{proof.lastError}</p>
            </div>
          )}
        </div>
      </Dialog>
    </section>
  )
}
